(function () {
  class MavenVideoStory extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.mediaQuery = window.matchMedia('(min-width: 768px)');
      this.items = Array.from(this.querySelectorAll('[data-story-media]'));
      this.videos = this.items.map((item) => item.querySelector('video')).filter(Boolean);
      this.onToggle = this.onToggle.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onFirstInteraction = this.onFirstInteraction.bind(this);
      this.onVideoReady = this.onVideoReady.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.updateControls = this.updateControls.bind(this);

      this.querySelectorAll('[data-story-toggle]').forEach((button) => {
        button.addEventListener('click', this.onToggle);
      });
      this.videos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.autoplay = true;
        video.loop = true;
        video.setAttribute('muted', '');
        video.setAttribute('defaultmuted', '');
        video.setAttribute('autoplay', '');
        video.setAttribute('loop', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('disablepictureinpicture', '');
        video.preload = 'auto';
        video.addEventListener('play', this.updateControls);
        video.addEventListener('pause', this.updateControls);
        video.addEventListener('loadedmetadata', this.onVideoReady);
        video.addEventListener('loadeddata', this.onVideoReady);
        video.addEventListener('canplay', this.onVideoReady);
      });

      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onMediaChange);
      } else {
        this.mediaQuery.addListener(this.onMediaChange);
      }
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('load', this.onPageShow);
      document.addEventListener('pointerdown', this.onFirstInteraction, { once: true, passive: true });
      document.addEventListener('touchstart', this.onFirstInteraction, { once: true, passive: true });
      document.addEventListener('keydown', this.onFirstInteraction, { once: true });
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.isVisible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.1;
          if (this.isVisible) {
            this.attemptAutoplay();
            this.scheduleAutoplayRetries();
          } else {
            this.clearAutoplayRetries();
            this.videos.forEach((video) => video.pause());
          }
        }, { threshold: [0, 0.1, 0.5] });
        this.observer.observe(this);
      }
      this.onViewportChange();
      this.updateControls();
    }

    disconnectedCallback() {
      this.querySelectorAll('[data-story-toggle]').forEach((button) => {
        button.removeEventListener('click', this.onToggle);
      });
      this.videos.forEach((video) => {
        video.removeEventListener('play', this.updateControls);
        video.removeEventListener('pause', this.updateControls);
        video.removeEventListener('loadedmetadata', this.onVideoReady);
        video.removeEventListener('loadeddata', this.onVideoReady);
        video.removeEventListener('canplay', this.onVideoReady);
      });
      if (this.mediaQuery) {
        if (this.mediaQuery.removeEventListener) {
          this.mediaQuery.removeEventListener('change', this.onMediaChange);
        } else {
          this.mediaQuery.removeListener(this.onMediaChange);
        }
      }
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('load', this.onPageShow);
      document.removeEventListener('pointerdown', this.onFirstInteraction);
      document.removeEventListener('touchstart', this.onFirstInteraction);
      document.removeEventListener('keydown', this.onFirstInteraction);
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      if (this.viewportFrame) window.cancelAnimationFrame(this.viewportFrame);
      this.clearAutoplayRetries();
      if (this.observer) this.observer.disconnect();
      this.initialized = false;
    }

    get activeItem() {
      if (this.items.length === 1) return this.items[0];
      const type = this.mediaQuery.matches ? 'desktop' : 'mobile';
      return this.items.find((item) => item.dataset.storyMedia === type) || this.items[0];
    }

    get activeVideo() {
      return this.activeItem ? this.activeItem.querySelector('video') : null;
    }

    onToggle(event) {
      const item = event.currentTarget.closest('[data-story-media]');
      const video = item ? item.querySelector('video') : this.activeVideo;
      if (!video) return;
      if (video.paused) {
        this.play(video);
      } else {
        video.pause();
      }
    }

    onMediaChange() {
      this.videos.forEach((video) => video.pause());
      if (this.isVisible) this.attemptAutoplay();
      this.updateControls();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.videos.forEach((video) => video.pause());
      } else if (this.isVisible) {
        this.attemptAutoplay();
      }
    }

    onPageShow() {
      if (this.isVisible) {
        this.attemptAutoplay();
        this.scheduleAutoplayRetries();
      }
    }

    onFirstInteraction() {
      if (this.isVisible) this.attemptAutoplay();
    }

    onVideoReady(event) {
      if (this.isVisible && event.currentTarget === this.activeVideo) this.attemptAutoplay();
    }

    onViewportChange() {
      if (this.viewportFrame) return;
      this.viewportFrame = window.requestAnimationFrame(() => {
        this.viewportFrame = null;
        const bounds = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const visibleHeight = Math.min(bounds.bottom, viewportHeight) - Math.max(bounds.top, 0);
        const visibleRatio = bounds.height > 0 ? visibleHeight / bounds.height : 0;
        const isInView = visibleHeight > 0 && visibleRatio >= 0.1;

        if (isInView && !this.isVisible) {
          this.isVisible = true;
          this.attemptAutoplay();
          this.scheduleAutoplayRetries();
        } else if (!isInView && this.isVisible) {
          this.isVisible = false;
          this.clearAutoplayRetries();
          this.videos.forEach((video) => video.pause());
        } else if (isInView) {
          const video = this.activeVideo;
          if (video && video.paused) this.attemptAutoplay();
        }
      });
    }

    attemptAutoplay() {
      if (this.dataset.autoplay !== 'true' || document.hidden) return;
      const video = this.activeVideo;
      if (video) this.play(video);
    }

    scheduleAutoplayRetries() {
      this.clearAutoplayRetries();
      this.retryTimers = [250, 900, 1800].map((delay) => window.setTimeout(() => {
        const video = this.activeVideo;
        if (this.isVisible && video && video.paused) this.attemptAutoplay();
      }, delay));
    }

    clearAutoplayRetries() {
      if (!this.retryTimers) return;
      this.retryTimers.forEach((timer) => window.clearTimeout(timer));
      this.retryTimers = [];
    }

    play(video) {
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('defaultmuted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      this.videos.forEach((item) => {
        if (item !== video) item.pause();
      });
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => this.updateControls());
      }
    }

    updateControls() {
      this.items.forEach((item) => {
        const video = item.querySelector('video');
        const button = item.querySelector('[data-story-toggle]');
        if (!video || !button) return;
        const playing = !video.paused && !video.ended;
        button.dataset.state = playing ? 'playing' : 'paused';
        button.setAttribute('aria-label', playing ? button.dataset.pauseLabel : button.dataset.playLabel);
        button.setAttribute('aria-pressed', String(playing));
      });
    }
  }

  if (!customElements.get('maven-video-story')) {
    customElements.define('maven-video-story', MavenVideoStory);
  }
})();
